import { useEvent } from '@emerald-works/react-event-bus-client'
import { ArrowForward, Check, ChevronRight, RocketLaunchTwoTone } from '@mui/icons-material'
import { AppBar, Box, Breadcrumbs, Chip, LinearProgress, Link as MuiLink, Skeleton, Toolbar, Typography } from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import accelerateSlice from '../../reducers/accelerate'
import { routes } from '../../router/routes'
import { FocusIcon } from '../icons/focus-icon'
import { useStyles } from './style'

export const ACCELERATE_STATUSES = {
  NOT_ENABLED: 'NOT_ENABLED',
  ASSESSMENT_PENDING: 'ASSESSMENT_PENDING',
  ASSESSMENT_IN_PROGRESS: 'ASSESSMENT_IN_PROGRESS',
  ASSESSMENT_COMPLETED: 'ASSESSMENT_COMPLETED',
  REPORT_VIEWED: 'REPORT_VIEWED',
  SKILL_BUILDER_IN_PROGRESS: 'SKILL_BUILDER_IN_PROGRESS',
  SKILL_BUILDER_COMPLETED: 'SKILL_BUILDER_COMPLETED'
}

const ASSESS_STATUSES = new Set([
  ACCELERATE_STATUSES.NOT_ENABLED,
  ACCELERATE_STATUSES.ASSESSMENT_PENDING,
  ACCELERATE_STATUSES.ASSESSMENT_IN_PROGRESS,
  ACCELERATE_STATUSES.ASSESSMENT_COMPLETED // Report viewing is part of Assess phase
])

const BUILD_STATUSES = new Set([
  ACCELERATE_STATUSES.REPORT_VIEWED,
  ACCELERATE_STATUSES.SKILL_BUILDER_IN_PROGRESS
])

const getJourneyStepsFromStatus = (status) => {
  const isAssessPhase = ASSESS_STATUSES.has(status)
  const isBuildPhase = BUILD_STATUSES.has(status)
  const isApplyPhase = status === ACCELERATE_STATUSES.SKILL_BUILDER_COMPLETED

  return [
    { label: 'Assess Your Skills', status: isAssessPhase ? 'current' : 'complete' },
    { label: 'Build Your Skills', status: isBuildPhase ? 'current' : isAssessPhase ? 'upcoming' : 'complete' },
    { label: 'Apply Your Skills', status: isApplyPhase ? 'current' : 'upcoming' }
  ]
}

const SkeletonLoader = ({ classes }) => (
  <AppBar
    position='sticky'
    className={classes.root}
    elevation={0}
    data-test='accelerate-context-bar-skeleton'
  >
    <Toolbar className={classes.toolbar} disableGutters>
      <Box className={classes.container}>
        <Box className={classes.leftSection}>
          <Skeleton variant='circular' width={24} height={24} />
          <Skeleton variant='text' width={80} height={24} />
        </Box>
        <Box className={classes.rightSection}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Skeleton variant='circular' width={20} height={20} />
            <Skeleton variant='text' width={40} height={16} />
            <Box sx={{ width: 24, height: 1, bgcolor: 'grey.300' }} />
            <Skeleton variant='circular' width={20} height={20} />
            <Skeleton variant='text' width={32} height={16} />
            <Box sx={{ width: 24, height: 1, bgcolor: 'grey.300' }} />
            <Skeleton variant='circular' width={20} height={20} />
            <Skeleton variant='text' width={36} height={16} />
          </Box>
        </Box>
      </Box>
    </Toolbar>
  </AppBar>
)

const NextStepChip = ({ nextStep, classes, onNextStepClick }) => {
  if (!nextStep) return null

  return (
    <>
      <Box className={classes.nextStepContainer}>
        <Typography className={classes.nextStepLabel}>Next:</Typography>
        <Chip
          label={nextStep.label}
          onClick={onNextStepClick}
          onDelete={onNextStepClick}
          deleteIcon={<ArrowForward className={classes.nextStepIcon} />}
          className={classes.nextStepChip}
          size='small'
          clickable
        />
      </Box>
      <Box className={classes.separator} />
    </>
  )
}

const JourneyStep = ({ step, isLast, classes }) => {
  const circleClassName = `${classes.journeyStepCircle} ${step.status === 'complete'
    ? classes.journeyStepComplete
    : step.status === 'current'
      ? classes.journeyStepCurrent
      : classes.journeyStepUpcoming
    }`

  return (
    <>
      <Box className={classes.journeyStep}>
        <Box className={circleClassName}>
          {step.status === 'complete' && <Check className={classes.journeyStepIcon} />}
          {step.status === 'current' && <FocusIcon className={classes.focusIcon} />}
        </Box>
        <Typography className={classes.journeyStepLabel}>{step.label}</Typography>
      </Box>
      {!isLast && <Box className={classes.journeyStepConnector} />}
    </>
  )
}

const BreadcrumbItem = ({ crumb, isLast, classes }) => {
  const CrumbIcon = crumb.icon
  const iconStyle = { display: 'flex', alignItems: 'center', gap: 0.5 }

  if (isLast || !crumb.path) {
    return (
      <Box
        key={crumb.label}
        className={classes.breadcrumbText}
        data-test={`breadcrumb-${crumb.label}`}
        sx={iconStyle}
      >
        {CrumbIcon && <CrumbIcon />}
        <Typography component='span'>{crumb.label}</Typography>
      </Box>
    )
  }

  return (
    <MuiLink
      key={crumb.label}
      component={Link}
      to={crumb.path}
      className={classes.breadcrumbLink}
      underline='hover'
      data-test={`breadcrumb-link-${crumb.label}`}
      sx={iconStyle}
    >
      {CrumbIcon && <CrumbIcon />}
      {crumb.label}
    </MuiLink>
  )
}

const AccelerateContextBar = ({
  breadcrumbs = [],
  progress = 0,
  progressLabel = 'My Journey',
  variant = 'subtle',
  nextStep = null
}) => {
  const accelerateStatus = useSelector(accelerateSlice.selectors.selectStatus)
  const progressData = useSelector(accelerateSlice.selectors.selectProgress)

  const classes = useStyles({ variant })
  const [loading, setLoading] = useState(false)
  const [getAccelerateStatus] = useEvent([accelerateSlice.eventBus.getAccelerateStatus({
    onSuccess: () => {
      setLoading(false)
    },
    onError: () => {
      setLoading(false)
    }
  })])

  useEffect(() => {
    if (!accelerateStatus) {
      setLoading(true)
      getAccelerateStatus.trigger()
    }
  }, [accelerateStatus, getAccelerateStatus])

  const journeySteps = useMemo(
    () => (accelerateStatus ? getJourneyStepsFromStatus(accelerateStatus) : null),
    [accelerateStatus]
  )

  const handleNextStepClick = () => nextStep?.onClick?.()

  const hasBreadcrumbs = breadcrumbs.length > 0

  if (loading) {
    return <SkeletonLoader classes={classes} />
  }

  return (
    <AppBar
      position='sticky'
      className={classes.root}
      elevation={0}
      data-test='accelerate-context-bar'
    >
      <Toolbar className={classes.toolbar} disableGutters>
        <Box className={classes.container}>
          <Box className={classes.leftSection}>
            <Box className={classes.logo}>
              <RocketLaunchTwoTone color='primary' style={{ fontSize: 18 }} />
            </Box>
            <MuiLink
              component={Link}
              to={routes.accelerate.path}
              className={classes.programNameLink}
              underline='hover'
              data-test='program-name-link'
            >
              <Typography className={classes.programName}>Accelerate</Typography>
            </MuiLink>
            {hasBreadcrumbs && (
              <>
                <ChevronRight className={classes.breadcrumbSeparator} />
                <Breadcrumbs
                  separator={<ChevronRight className={classes.breadcrumbSeparator} />}
                  aria-label='breadcrumb'
                  className={classes.breadcrumbs}
                >
                  {breadcrumbs.map((crumb, index) => (
                    <BreadcrumbItem
                      key={crumb.label}
                      crumb={crumb}
                      isLast={index === breadcrumbs.length - 1}
                      classes={classes}
                    />
                  ))}
                </Breadcrumbs>
              </>
            )}
          </Box>

          <Box className={classes.rightSection}>
            <NextStepChip
              nextStep={nextStep}
              classes={classes}
              onNextStepClick={handleNextStepClick}
            />
            {journeySteps
              ? (
                <Box className={classes.journeyStepsContainer}>
                  {journeySteps.map((step, index) => (
                    <JourneyStep
                      key={step.label}
                      step={step}
                      isLast={index === journeySteps.length - 1}
                      classes={classes}
                    />
                  ))}
                </Box>
                )
              : (
                <>
                  <Typography className={classes.progressLabel}>{progressLabel}</Typography>
                  <Box className={classes.progressContainer}>
                    <LinearProgress
                      variant='determinate'
                      value={progressData?.percentage ?? 0}
                      className={classes.progressBar}
                    />
                    <Typography className={classes.progressValue}>{progress}%</Typography>
                  </Box>
                </>
                )}
          </Box>
        </Box>
      </Toolbar>
    </AppBar>
  )
}

export default AccelerateContextBar
